import useApi from 'vl-common/src/hooks/useApi';
import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Typography } from 'antd';
import { AsyncAntdButton as Button } from './AsyncComponents';
import { Note } from 'vl-common/src/api/api.notes';
import useDispatchWithSpinners from 'vl-common/src/hooks/useDispatchWithSpinners';
import { errorModal } from 'vl-common/src/components/Modals';
import { NoteWithReplies } from './PatientNote';
import { useTranslation } from 'react-i18next';

interface NoteEditorProps {
  patient_guid: string; // the patient to which the note (will) apply to
  note?: NoteWithReplies; // the current note properties if editing an existing note
  mode: 'reply' | 'edit' | 'add'; // the mode (either adding a new note to the thread or amending an existing one)
  onClosed: () => void; // invoked when the editor decides it should be closed
  case_id?: number;
}

// Textarea with save & cancel buttons and the "shared with clinician" checkbox. Saves notes itself.
export const NoteEditor: React.FC<NoteEditorProps> = ({ patient_guid, note, mode, onClosed, case_id }) => {
  const { t } = useTranslation('common');
  const { Api, consumed } = useApi();
  const queryClient = useQueryClient();
  const { dispatchWithSpinner } = useDispatchWithSpinners();
  const [noteText, setNoteText] = useState(note && mode === 'edit' ? note.note_text : '');
  const isNoteClinVisible = !!note?.clin_visible;
  // For the 'add' mode 'sharedWithClinicians' is set as true.
  // For the 'edit' mode 'sharedWithClinicians' depends on 'clin_visible' value of the note user is going to edit.
  // For the 'reply' mode 'sharedWithClinicians' depends on 'clin_visible' value of the note user is going to add a reply.
  const [sharedWithClinicians, setSharedWithClinicians] = useState<boolean>(
    mode === 'add' || (mode === 'edit' && isNoteClinVisible) || (mode === 'reply' && isNoteClinVisible)
  );

  // For the 'add' mode 'Shared with clinicians' checkbox is always visible.
  // For the 'edit' mode if current note is a conversation starter
  // then 'Shared with clinicians' checkbox is always visible.
  // For the 'edit' mode if current note is a reply
  // then 'Shared with clinicians' visibility depends on 'clin_visible' value of the note's parent.
  // For the 'reply' then 'Shared with clinicians' visibility depends on
  // 'clin_visible' value of the note user is going to add a reply.
  const showSharedWithCliniciansCheckbox =
    mode === 'add' ||
    (mode === 'edit' && note?.isNoteConversationStarter) ||
    (mode === 'edit' && note?.isParentNoteClinVisible) ||
    (mode === 'reply' && isNoteClinVisible);

  const send = async (noteData: Partial<Note>) => {
    await dispatchWithSpinner(Api.notes.postPatientNotes, [patient_guid, { patient_guid, case_id, ...noteData }]);
  };

  const handler = async () => {
    try {
      if (mode === 'add') {
        await send({
          note_text: noteText,
          clin_visible: sharedWithClinicians
        });
      }

      if (mode === 'edit') {
        await send({
          note_text: noteText,
          clin_visible: sharedWithClinicians,
          parent_id: note?.parent_id,
          note_id: note?.note_id
        });
      }

      if (mode === 'reply') {
        await send({
          note_text: noteText,
          clin_visible: sharedWithClinicians,
          parent_id: note?.note_id
        });
      }
    } catch (err) {
      // @ts-ignore
      const message = err?.response?.data?.message;
      const titleMap: Record<typeof mode, string> = {
        reply: 'The reply has not been added',
        add: 'The note has not been added',
        edit: 'The note has not been edited'
      };
      errorModal({
        title: titleMap[mode],
        content: <Typography>{message}</Typography>
      });
      consumed(err);
    } finally {
      onClosed();
      await queryClient.invalidateQueries({
        queryKey: ['getPatientNotes']
      });
    }
  };

  return (
    <div className="note-editor">
      <textarea defaultValue={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2} />
      <div className="button-row">
        <Button onClick={onClosed} data-testid="cancel-note-button">
          {t('Cancel')}
        </Button>
        {showSharedWithCliniciansCheckbox && (
          <label>
            {t('Shared with clinicians?')}
            <input type="checkbox" checked={sharedWithClinicians} onChange={() => setSharedWithClinicians((v) => !v)} />
          </label>
        )}
        <Button onClick={handler} disabled={!noteText} data-testid="send-note-button">
          {t('Send')}
        </Button>
      </div>
    </div>
  );
};

export default NoteEditor;
